import type { Audit, AuditCoverage, AuditScope, AuditTally, Op, Session } from './types'
import { auditLive, auditTally } from './types'
import type { ProofObligation } from './mathematics'

export const AUDIT_SCOPES: readonly AuditScope[] = [
  'statement',
  'derivation',
  'assumptions',
  'scope',
  'novelty',
  'relevance',
]
export const AUDIT_VERDICTS: readonly Audit['verdict'][] = [
  'pass',
  'objection',
  'insufficient-context',
]

export function auditFindingLabel(audit: Audit): string {
  const finding =
    audit.verdict === 'objection'
      ? 'Proposed criticism'
      : audit.verdict === 'pass'
        ? 'No specific objection'
        : 'Insufficient context'
  return `${finding} · ${audit.scope ?? 'historical scope unrecorded'}${audit.proofStep ? ` · ${audit.proofStep.obligationId}/${audit.proofStep.stepId ?? 'statement'} at ${audit.proofStep.revision}` : ''}`
}

const s = (n: number): string => (n === 1 ? '' : 's')

/**
 * What a set of scoped reviews came to, as one sentence: "1 of 5 completed
 * reviews by GPT-6 Astra found no specific objection; 4 proposed criticisms
 * need checking." The report's "Scoped reviews by another provider" section
 * and the card's cross-examination press both print it, so a reader meets the
 * counted result where they pressed for it and not only in a download.
 *
 * Arithmetic over recorded verdicts (`auditTally`), as the report promises: no
 * model is asked to tally, and none is asked what the tally means. A review
 * that lacked context stays outside the count and is said to.
 */
export function auditTallySentence(t: AuditTally): string {
  if (!t.auditors.length) return ''
  const by = t.auditors.join(' and ')
  const head = t.examined
    ? `${t.noObjection} of ${t.examined} completed review${s(t.examined)} by ${by} found no specific objection${
        t.objections
          ? `; ${t.objections} proposed criticism${s(t.objections)} need${t.objections === 1 ? 's' : ''} checking`
          : ''
      }.`
    : `No review by ${by} reached a verdict.`
  const context = t.needsContext
    ? ` ${t.needsContext} review${s(t.needsContext)} lacked sufficient context and ${t.needsContext === 1 ? 'remains' : 'remain'} outside this count.`
    : ''
  return head + context
}

/**
 * The newest cross-examination of this branch that is not the pass in flight.
 *
 * Not simply the newest: coverage is written before dispatch, so a batch still
 * running has every row `incomplete` — and the card read that as "0/5" under a
 * "Continue unfinished reviews" label, a failure that had not happened. The
 * feed's own rule tells the two apart (`feedItems`): only the newest pass can be
 * live, and only while the lock is held. An older unfinished batch is one a tab
 * closed under, and its unfinished reviews are exactly what a continuation is
 * for, so it stays.
 */
export function lastSettledAudit(
  session: Pick<Session, 'ops'>,
  rootId: string,
  busy: boolean,
): Op | undefined {
  const live = busy ? session.ops.filter((op) => !op.narration).at(-1) : undefined
  return session.ops.findLast(
    (op) => op.kind === 'audit' && op.targetIds[0] === rootId && op !== live,
  )
}

/** One settled batch, counted: the live verdicts its reviews left on their
 *  claims, and the reviews it asked for that did not finish. */
export interface AuditBatch {
  tally: AuditTally
  unfinished: number
  requested: number
}

export function auditBatch(session: Pick<Session, 'nodes'>, op: Op): AuditBatch | null {
  const coverage = op.auditCoverage
  if (!coverage) return null
  const audits: Audit[] = []
  let unfinished = 0
  for (const row of coverage.claims) {
    if (row.research === 'incomplete') {
      unfinished++
      continue
    }
    // A claim pruned or rewritten since has no verdict that describes it now —
    // the export's own rule (`auditLive`), so the two sentences count alike.
    const node = session.nodes[row.nodeId]
    if (node?.audit && auditLive(node)) audits.push(node.audit)
  }
  return { tally: auditTally(audits), unfinished, requested: coverage.requested.length }
}

/** The card's line for a settled batch: the export's sentence, then what did
 *  not finish — which is what "Continue unfinished reviews" continues. */
export function auditBatchSentence(b: AuditBatch): string {
  const unfinished = b.unfinished
    ? `${b.unfinished} of ${b.requested} review${s(b.requested)} did not finish.`
    : ''
  return [auditTallySentence(b.tally), unfinished].filter(Boolean).join(' ')
}

/** A malformed response cannot pass a claim by default. */
export function parseAuditFinding(
  value: Record<string, unknown>,
  nodeId: string,
  mathematicalTargets: ProofObligation[] = [],
) {
  const returnedId = typeof value.nodeId === 'string' ? value.nodeId.replace(/^\[|\]$/g, '') : ''
  if (
    returnedId !== nodeId ||
    !AUDIT_VERDICTS.includes(value.verdict as Audit['verdict']) ||
    !AUDIT_SCOPES.includes(value.scope as AuditScope) ||
    typeof value.note !== 'string' ||
    !value.note.trim() ||
    value.note.length > 4000
  )
    return null
  if (value.verdict === 'objection') {
    const objection = value.objection as Record<string, unknown> | null
    if (
      !objection ||
      !['label', 'claim', 'reasoning'].every(
        (key) => typeof objection[key] === 'string' && (objection[key] as string).trim(),
      )
    )
      return null
  } else if (value.objection !== null) return null
  let proofStep: Audit['proofStep']
  if (value.proofStep !== undefined && value.proofStep !== null) {
    const p = value.proofStep as Audit['proofStep']
    if (
      !p ||
      typeof p.obligationId !== 'string' ||
      typeof p.revision !== 'string' ||
      (p.stepId !== null && typeof p.stepId !== 'string')
    )
      return null
    const target = mathematicalTargets.find(
      (o) => o.id === p.obligationId && o.revision === p.revision && o.nodeId === nodeId,
    )
    if (!target || (p.stepId !== null && !target.steps.some((s) => s.id === p.stepId))) return null
    if (value.scope === 'derivation' && p.stepId === null) return null
    proofStep = { obligationId: p.obligationId, revision: p.revision, stepId: p.stepId }
  }
  if (
    mathematicalTargets.length &&
    value.verdict === 'objection' &&
    !['novelty', 'relevance'].includes(String(value.scope)) &&
    !proofStep
  )
    return null
  return {
    verdict: value.verdict as Audit['verdict'],
    scope: value.scope as AuditScope,
    note: value.note,
    objection: value.objection,
    ...(proofStep ? { proofStep } : {}),
  }
}

/** Coverage is independent of a model's opinion. An interrupted paid request
 * stays unknown; this parser never promotes it after a reload. */
export function parseAuditCoverage(value: unknown): AuditCoverage | null {
  if (!value || typeof value !== 'object') return null
  const v = value as AuditCoverage
  const ids = (x: unknown): x is string[] =>
    Array.isArray(x) &&
    x.length <= 12 &&
    x.every((id) => typeof id === 'string' && id.length > 0 && id.length <= 60) &&
    new Set(x).size === x.length
  if (
    v.version !== 1 ||
    !ids(v.requested) ||
    !ids(v.skipped) ||
    (v.continuationOf !== undefined &&
      (typeof v.continuationOf !== 'string' || v.continuationOf.length > 60)) ||
    !Array.isArray(v.claims) ||
    v.claims.length !== v.requested.length ||
    !v.claims.every(
      (c, i) =>
        c &&
        c.nodeId === v.requested[i] &&
        (c.callId === undefined || (typeof c.callId === 'string' && c.callId.length <= 120)) &&
        ['not-started', 'unknown', 'completed', 'refused', 'failed'].includes(c.transport) &&
        (c.truncated === null || typeof c.truncated === 'boolean') &&
        ['missing', 'invalid', 'valid'].includes(c.structured) &&
        ['incomplete', 'insufficient-context', 'completed'].includes(c.research) &&
        (c.research === 'incomplete' ||
          (c.transport === 'completed' && c.truncated === false && c.structured === 'valid')),
    )
  )
    return null
  return {
    version: 1,
    requested: [...v.requested],
    skipped: [...v.skipped],
    ...(v.continuationOf !== undefined ? { continuationOf: v.continuationOf } : {}),
    claims: v.claims.map((c) => ({
      nodeId: c.nodeId,
      ...(c.callId !== undefined ? { callId: c.callId } : {}),
      transport: c.transport,
      truncated: c.truncated,
      structured: c.structured,
      research: c.research,
    })),
  }
}
